<template>
  <div class="product-link">
    <div class="product-link-top">
      Products in this Campaign ({{ pagination.total }})
      <div class="product-export" @click="exportReporting">
        <Icon name="daochu" class="product-export-icon" />
        <span>Export Product Links</span>
      </div>
    </div>
    <div class="mid-filter">
      <div class="mid-filter-item">
        <div class="mid-filter-title">Deals</div>
        <div class="mid-filter-checkbox">
          <a-checkbox v-model:checked="filter.activeDeals" @change="setFilter">Active</a-checkbox>
          <a-checkbox v-model:checked="filter.upcomingDeals" @change="setFilter"
            class="upcoming-box">Upcoming</a-checkbox>
        </div>
      </div>
    </div>
    <div class="table-wrapper">
      <artmisads-table :columns="publisherColumns" :data-source="publisherRows" :change="onTableChange" :pagination="{
        total: pagination.total,
        current: pagination.current,
        pageSize: pagination.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30', '40', '50']
      }" :loading="publisherListLoading" :scroll="{ x: 1200}" class="publisher-table">
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.key === 'product'">
            <div class="product-info" @click="goProduct(record)">
              <LogoImage :logoUrl="record.imageUrl" :brandName="record.brandName" />
              <div class="product-info-text">
                <a-tooltip :title="record.productTitle || undefined" placement="topLeft">
                  <div class="product-title">{{ record.productTitle }}</div>
                </a-tooltip>
                <div class="product-asin">{{ record.asin }}</div>
              </div>
            </div>
          </template>
          <template v-if="column.key === 'deals'">
            <product-deals-column v-if="record.productDeals?.length" v-for="item in record.productDeals" :record="item"
              :key="item.id" />
            <span v-if="!record.productDeals?.length" class="deal-empty">—</span>
          </template>
          <template v-if="column.key === 'price'">
            <Price :record="record" />
          </template>
          <template v-if="column.key === 'attributionCommission'">
            {{ record.attributionCommission }}%
          </template>
          <template v-if="column.key === 'attributionLink'">
            <template v-if="item.attributionCpcEnabled">
              <div v-if="record.attributionLink" class="link-cell">
              <span class="link-text">{{ record.attributionLink }}</span>
              <Icon name="copy2-cu" class="copy-icon" @click="copyTextToClipboard(record.attributionLink)" />
            </div>
            <div v-else>
              Link Generate Failed 
              <Icon name="gengxin" class="icon-gengxin" @click="generateLink(record.productId,'attribution')" />
            </div>
            </template>
            <template v-else>
              N/A
            </template>
          </template>
          <template v-if="column.key === 'associateLink'">
            <template v-if="item.associateCpcEnabled">
            <div v-if="record.associateLink" class="link-cell">
              <span class="link-text">{{ record.associateLink }}</span>
              <Icon name="copy2-cu" class="copy-icon" @click="copyTextToClipboard(record.associateLink)" />
            </div>
            <div v-else>
              Link Generate Failed 
              <Icon name="gengxin" class="icon-gengxin" @click="generateLink(record.productId,'associate')" />
            </div>
          </template>
          <template v-else>
            N/A
          </template>
          </template>
          <template v-if="column.key === 'action'">
            <div class="action-cell">
              <artmisads-select-button class="copy-link-button" size="small" @click="openCopyLinkModal(record)">
                <Icon name="lianjie" class="copy-icon" />
                Copy Link
              </artmisads-select-button>
            </div>
          </template>
        </template>
      </artmisads-table>
    </div>

    <CopyLinkModal
      ref="copyLinkModalRef"
      :attribution-supported="Boolean(item?.attributionCpcEnabled)"
      :associate-supported="Boolean(item?.associateCpcEnabled)"
      :campaign-id="campaignId"
      @regenerated="onLinkRegenerated"
    />
  </div>
</template>
<script setup>
import Icon from '@/components/Icon.vue'
import { cpcCampaignsApi } from '~/server'
import { useRoute } from 'vue-router'
import { ref, onMounted } from 'vue'
import LogoImage from '@/components/LogoImage.vue'
import ProductDealsColumn from "~/components/productDealsColumn.vue"
import Price from "~/components/Price.vue"
import CopyLinkModal from './copyLinkModal.vue'
import { message } from 'ant-design-vue'
import { copyToClipboard } from '~/utils'
import { useRouter } from 'vue-router'
const router = useRouter()
const route = useRoute()
const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  }
})
const campaignId = route.params.id
const copyLinkModalRef = ref(null)
const filter = ref({
  activeDeals: false,
  upcomingDeals: false,
})
const goProduct = (record) => {
  router.push({
    path: `/publisher/products/${record.productId}`,
  })
}
const exportReporting = async () => {
  await cpcCampaignsApi.exportProductLinks('Product Links', { campaignId: campaignId })
}
const setFilter = (value) => {
  filter.value[value] = !filter.value[value]
  getProductLinkList()
}
const pagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
})
const copyTextToClipboard = (link) => {
  if (!link) return
  copyToClipboard(link)
  message.success('Link copied to clipboard')
}

const openCopyLinkModal = (record) => {
  copyLinkModalRef.value?.show(record)
}

const onLinkRegenerated = async ({ productId }) => {
  await getProductLinkList()
  const updated = publisherRows.value.find((row) => row.productId === productId)
  if (updated) copyLinkModalRef.value?.updateRecord(updated)
}

const generateLink = (productId, linkType) => {
  cpcCampaignsApi.retryGenerateProductLinks({ productId, linkType, campaignId }).then((res) => {
    if (res.success) {
      message.success('Link generated successfully')
      getProductLinkList()
    } else {
      message.error(res.error)
    }
  })
}
const publisherColumns = ref([
  {
    title: 'Product',
    key: 'product',
    width: 400,
  },
  {
    title: 'Brand',
    key: 'brandName',
    dataIndex: 'brandName',
    width: 300,
  },
  {
    title: 'Category',
    key: 'primaryCategory',
    dataIndex: 'primaryCategory',
    width: 300,
  },
  {
    title: 'Deals',
    key: 'deals',
    width: 250,
  },
  {
    title: 'Price',
    key: 'price',
    width: 150,
  },
  {
    title: 'AttributionCommission',
    key: 'attributionCommission',
    width: 200,
  },
  {
    title:'Action',
    key:'action',
    width: 150,
    fixed: 'right',
  }
  // {
  //   title: 'AttributionLink',
  //   key: 'attributionLink',
  //   width: 300,
  // },
  // {
  //   title: 'AssociateLink',
  //   key: 'associateLink',
  //   width: 300,
  // },
])
const publisherRows = ref([])
const publisherListLoading = ref(false)
const onTableChange = (pag) => {
  if (!pag) return
  pagination.value.current = pag.current ?? pagination.value.current
  pagination.value.pageSize = pag.pageSize ?? pagination.value.pageSize
  if (pag.total != null) {
    pagination.value.total = pag.total
  }
  getProductLinkList()
}
const getProductLinkList = async () => {
  publisherListLoading.value = true
  let dealStatuses = []
  if(filter.value.activeDeals){
    dealStatuses.push('Active')
  }
  if(filter.value.upcomingDeals){
    dealStatuses.push('Upcoming')
  }
  try {
    const { data } = await cpcCampaignsApi.getJoinedProductLinks(
      { campaignId: campaignId,
        page:pagination.value.current,
        size: pagination.value.pageSize,
        dealStatuses: dealStatuses.join(',')
      })
    publisherRows.value = data.data
    pagination.value.total = data.total
  } finally {
    publisherListLoading.value = false
  }
}
onMounted(() => {
  getProductLinkList()
})
</script>
<style scoped lang="less">
.copy-link-button{
  display: flex;
  align-items: center;
  gap: 4px;
  color: #7d64ff !important;

}
.product-link {
  .link-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    width: 400px;
    max-width: 400px;
  }
  .link-text {
    display: block;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .copy-icon {
    flex-shrink: 0;
    cursor: pointer;
  }
  .icon-gengxin{
    flex-shrink: 0;
    cursor: pointer;
  }
  .deal-empty {
  color: #9ca3af;
}
  .product-info {
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 400px;
    cursor: pointer;
    .product-info-text {
      min-width: 0;
      flex: 1;

      .product-title {
        font-family: Sora;
        font-weight: 500;
        font-size: 14px;
        line-height: 22px;
        letter-spacing: 0.4px;
        text-decoration: underline;
        text-decoration-style: solid;
        color: rgba(15, 6, 41, 1);
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        overflow: hidden;
        word-break: break-word;
      }

      .product-asin {
        font-family: Sora;
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0.3px;
        color: rgba(15, 6, 41, 0.5);
      }
    }

  }

  .mid-filter {
    display: flex;
    align-items: space-between;
    position: relative;
    margin-bottom: 16px;
    margin-top: 16px;
    // justify-content: space-between;
    padding: 16px;
    padding-right: 96px;
    border-radius: 8px;
    background-color: var(--bg-gray-1);
    flex-wrap: wrap;
    gap: 16px; // 垂直和水平间距都是16px
    // overflow: hidden;
    max-height: 86px; // 限制为一行的高度，不包括gap，因为第一行不会有gap
    transition: max-height 0.3s ease;

    &.expanded {
      max-height: none; // 展开时移除高度限制
      overflow: visible;
    }

    &-item {
      display: flex;
      flex-direction: column;
      // width: 20%;

      &.filter-item-hidden {
        display: none !important;
      }
    }

    //  &-item:first-child{
    //  width: 20%;
    //  }
    //  &-item:nth-child(2){
    //   width: 14%;
    //  }
    &-checkbox {
      background-color: var(--bg-gray-6);
      border-radius: 8px;
      height: 32px;
      padding: 0 8px;
      display: flex;
      align-items: center;
      // justify-content: center;
    }

    &-title {
      font-size: 13px;
      font-weight: 600;
      height: 18px;
      // opacity: 0.7;
      letter-spacing: 0.3px;
      line-height: 18px;
      color: #0f0629;
      margin-bottom: 4px;
    }
  }

  .product-link-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: Sora;
    font-weight: 600;
    font-size: 20px;
    padding: 22px 16px;
    letter-spacing: 0.4px;
    color: rgba(15, 6, 41, 1);
    border-bottom: 1px solid rgba(232, 229, 237, 1);
  }

  .product-export {
    display: flex;
    align-items: center;
    border-radius: 8px;
    background-color: rgba(114, 64, 255, 0.14);
    padding: 6px 16px;
    gap: 8px;
    cursor: pointer;

    .product-export-icon {
      width: 16px;
      height: 16px;
      color: rgba(15, 6, 41, 1);
    }

    >span {
      font-family: Sora;
      font-weight: 500;
      font-style: Medium;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.4px;
      color: rgba(15, 6, 41, 1);
    }
  }
}
</style>